import { useState } from 'react';
import type { InboxLaborRow } from '@coffman-tm/shared';
import { Pencil, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { InlineNumberInput } from './InlineNumberInput';
import { InlineClassSelect } from './InlineClassSelect';
import { InlineDaypartSelect } from './InlineDaypartSelect';
import { formatMoneyCents } from '../../lib/format';
import { rateForDaypart, type LaborEdit } from './laborEdits';
import { lineCents } from './blockSubtotals';
import { printedIdMarkerFor } from './rememberPlaceholder';

export { initialLaborEdits } from './laborEdits';
export type { LaborEdit };

/**
 * One row per labor entry in the block. Editable when block_status='pending';
 * view-only otherwise. Hours + rate edits flow through the parent `onChange`
 * so the block card can collect the bundle when Joey hits "Save to PCO".
 * The Class cell writes directly to employees.classification (project-wide)
 * via api.settings.setEmployeeClassification — classification is a property
 * of the employee, not the block, so it shouldn't wait on the block-save flow.
 */
export interface InboxLaborTableProps {
  rows: InboxLaborRow[];
  edits: Record<string, LaborEdit>;
  editable: boolean;
  onChange: (rowId: string, edit: LaborEdit) => void;
  /** Called after a successful class write so the parent refetches the
   *  block detail and the new classification appears in the row. */
  onClassChanged: () => Promise<void>;
  /** Open the Add-to-roster modal for an unknown employee row. */
  onAddToRoster: (row: InboxLaborRow) => void;
  /** Open the Reassign picker — point this line at an existing roster employee. */
  onReassign?: (row: InboxLaborRow) => void;
  /** Remove a manually-added labor line. */
  onRemoveManual?: (row: InboxLaborRow) => void;
}

export function InboxLaborTable({
  rows,
  edits,
  editable,
  onChange,
  onClassChanged,
  onAddToRoster,
  onReassign,
  onRemoveManual,
}: InboxLaborTableProps) {
  if (rows.length === 0) {
    return <div className="muted" style={{ fontSize: 12, padding: '6px 0' }}>No labor on this block.</div>;
  }
  return (
    <table style={tableStyle}>
      <colgroup>
        <col style={{ width: 100 }} />
        <col />
        <col style={{ width: 140 }} />
        <col style={{ width: 92 }} />
        <col style={{ width: 110 }} />
        <col style={{ width: 130 }} />
        <col style={{ width: 130 }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thStyle}>Emp ID</th>
          <th style={thStyle}>Name</th>
          <th style={thStyle}>Class</th>
          <th style={thStyle}>Shift</th>
          <th style={thStyleNum}>Total hrs</th>
          <th style={thStyleNum}>Rate</th>
          <th style={thStyleNum}>Total pay</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <LaborRow
            key={row.daily_labor_entry_id}
            row={row}
            edit={edits[row.daily_labor_entry_id]}
            editable={editable}
            onChange={onChange}
            onClassChanged={onClassChanged}
            onAddToRoster={onAddToRoster}
            onReassign={onReassign}
            onRemoveManual={onRemoveManual}
          />
        ))}
      </tbody>
    </table>
  );
}

function LaborRow({
  row,
  edit,
  editable,
  onChange,
  onClassChanged,
  onAddToRoster,
  onReassign,
  onRemoveManual,
}: {
  row: InboxLaborRow;
  edit: LaborEdit | undefined;
  editable: boolean;
  onChange: (rowId: string, edit: LaborEdit) => void;
  onClassChanged: () => Promise<void>;
  onAddToRoster: (row: InboxLaborRow) => void;
  onReassign?: (row: InboxLaborRow) => void;
  onRemoveManual?: (row: InboxLaborRow) => void;
}) {
  const { token } = useAuth();
  const [classBusy, setClassBusy] = useState(false);
  const [classErr, setClassErr] = useState<string | null>(null);
  const hours = edit?.total_hours ?? 0;
  const rate = edit?.rate_cents ?? 0;
  const daypart = edit?.daypart ?? row.primary_daypart;
  const total = lineCents(hours, rate);
  const canEditClass = editable && row.employee_id !== null;
  const printedIdMarker = printedIdMarkerFor(row);

  const handleClassSave = async (next: string) => {
    if (!token || !row.employee_id) return;
    if (next === row.classification) return;
    setClassBusy(true);
    setClassErr(null);
    try {
      await api.settings.setEmployeeClassification(token, row.employee_id, next);
      await onClassChanged();
    } catch (e) {
      setClassErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setClassBusy(false);
    }
  };

  return (
    <tr>
      <td style={tdStyleMono}>{row.hj_id ?? '—'}</td>
      <td style={tdStyle}>
        <span>{row.name}</span>
        {/* Row actions are icons, not text links. Every editable row offers
            Reassign and every unknown row offers Add-to-roster, so spelled-out
            labels repeated down the table buried the data they act on. */}
        {editable && onReassign && (
          <button
            type="button"
            className="icon-btn sm muted"
            style={{ marginLeft: 6 }}
            onClick={() => onReassign(row)}
            aria-label={`Reassign ${row.name} to a roster employee`}
            title="Reassign to a roster employee"
          >
            <Pencil size={13} aria-hidden />
          </button>
        )}
        {/* Audit of the line's original identity — shown whenever the roster
            link renders a different identity than the recorded raw text. For
            parsed rows that's the foreman's PDF text; for manual rows the raw
            is a roster snapshot from add time, not PDF text, so the label
            must not claim PDF provenance. */}
        {!row.is_unknown && row.employee_name_raw !== row.name && (
          <span
            className="muted"
            style={{ marginLeft: 8, fontSize: 11 }}
            title={
              row.is_manual
                ? 'As originally added to this block'
                : "As submitted on the foreman's PDF"
            }
          >
            {row.is_manual ? 'Originally:' : 'PDF:'} {row.employee_name_raw}
          </span>
        )}
        {/* What the report's ID column printed, when this line bills as a
            different ID — a hand reassign and an intake fill from a remembered
            crew slot render the SAME marker, because automation that repeats a
            decision must not be quieter on screen than the decision was.
            Suppressed when the note above already prints that exact string. */}
        {printedIdMarker !== null && (
          <span
            className="muted"
            style={{ marginLeft: 8, fontSize: 11 }}
            title="The ID printed on the foreman's report for this line"
          >
            PDF says {printedIdMarker}
          </span>
        )}
        {row.is_unknown && <UnknownChip />}
        {row.is_unknown && editable && (
          <button
            type="button"
            className="icon-btn sm positive"
            style={{ marginLeft: 4 }}
            onClick={() => onAddToRoster(row)}
            aria-label={`Add ${row.name} to the roster`}
            title="Add to roster"
          >
            <Plus size={14} aria-hidden />
          </button>
        )}
        {row.is_manual && (
          <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent)', background: 'var(--surface)', border: '1px solid var(--rule)', padding: '1px 6px', borderRadius: 3 }}>
            manually added
          </span>
        )}
        {row.is_manual && editable && onRemoveManual && (
          <button type="button" className="btn-link" style={{ marginLeft: 8, fontSize: 11, color: 'var(--danger)' }} onClick={() => onRemoveManual(row)} title="Remove this manually-added line">
            Remove
          </button>
        )}
      </td>
      <td style={tdStyle}>
        {canEditClass ? (
          <InlineClassSelect
            value={row.classification}
            onChange={handleClassSave}
            disabled={classBusy}
            ariaLabel={`${row.name} class`}
            width={130}
          />
        ) : (
          <span
            className="muted"
            title={row.is_unknown ? 'Unknown employee — add to roster in Settings to edit' : ''}
            style={ghostSelectStyle}
          >
            {row.classification}
          </span>
        )}
        {classErr && (
          <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{classErr}</div>
        )}
      </td>
      <td style={tdStyle}>
        {editable ? (
          <InlineDaypartSelect
            value={daypart}
            ariaLabel={`Shift for ${row.name}`}
            onChange={(next) => {
              const repriced = rateForDaypart(row, next);
              onChange(row.daily_labor_entry_id, {
                total_hours: hours,
                rate_cents: repriced,
                daypart: next,
              });
            }}
          />
        ) : (
          <span className="muted">{daypart === 'swing' ? 'Swing' : 'Day'}</span>
        )}
      </td>
      <td style={tdStyleNum}>
        {editable ? (
          <InlineNumberInput
            value={hours}
            decimals={1}
            onChange={(v) => onChange(row.daily_labor_entry_id, { total_hours: v, rate_cents: rate, daypart })}
            ariaLabel={`${row.name} total hours`}
          />
        ) : (
          <span className="mono">{hours.toFixed(1)}</span>
        )}
      </td>
      <td style={tdStyleNum}>
        {editable ? (
          <InlineNumberInput
            value={rate / 100}
            decimals={2}
            onChange={(v) =>
              onChange(row.daily_labor_entry_id, {
                total_hours: hours,
                rate_cents: Math.round(v * 100),
                daypart,
              })
            }
            ariaLabel={`${row.name} rate`}
          />
        ) : (
          <span className="mono">{formatMoneyCents(rate, { precise: true })}</span>
        )}
      </td>
      <td style={tdStyleNum}>
        <span className="mono">{formatMoneyCents(total, { precise: true })}</span>
      </td>
    </tr>
  );
}

function UnknownChip() {
  return (
    <span
      style={{
        marginLeft: 8,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--warning)',
        background: 'var(--surface-warm)',
        border: '1px solid var(--rule)',
        padding: '1px 6px',
        borderRadius: 3,
      }}
    >
      unknown
    </span>
  );
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '4px 8px',
  borderBottom: '1px solid var(--rule)',
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
};

const thStyleNum: React.CSSProperties = { ...thStyle, textAlign: 'right' };

const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--rule)',
  color: 'var(--ink)',
};

const tdStyleMono: React.CSSProperties = { ...tdStyle, fontFamily: 'var(--mono)', fontSize: 12 };

const tdStyleNum: React.CSSProperties = { ...tdStyle, textAlign: 'right' };

// Matches InlineClassSelect's box metrics (width/padding/border-radius/font)
// so the Class column baseline doesn't ragged out when a row has no select
// to render. Dashed border reads as "no control here" rather than a stray
// solid-bordered box implying the value is editable.
const ghostSelectStyle: React.CSSProperties = {
  display: 'inline-block',
  boxSizing: 'border-box',
  width: 130,
  padding: '4px 6px',
  border: '1px dashed var(--rule)',
  borderRadius: 3,
  fontFamily: 'var(--sans)',
  fontSize: 13,
};
